import { Button, Spin } from 'lego-on-react';
import block from 'propmods';
import React, { FC } from 'react';
import * as keyset from 'i18n/Resources';
import i18n from '@yandex-int/i18n';
import { moment } from '../../lib/moment';
import { ImageResource } from '../../model/resources';
import CopyField from '../CopyField/CopyField';
import './ImageTable.scss';
import Link from '../Link/Link';
import { ItemWrapper, ResourceError } from './ResourceManager';
import { formatBytes } from '../../lib/utils';

const t = i18n(keyset);
const b = block('ImageTable');

interface Props {
    remove: (id: string) => Promise<void>;
    items: Array<ItemWrapper<ImageResource>>;
}

const DocLink: FC = ({ children }) => {
    return (
        <Link to="https://tech.yandex.ru/dialogs/alice/doc/resource-upload-docpage" target="_blank">
            {children}
        </Link>
    );
};

const ImageTable: FC<Props> = (props) => {
    const { items, remove } = props;
    const { url: avatarsUrl } = __appConfig.avatars;

    const renderError = (error: ResourceError): React.ReactNode => {
        const message = error.message;
        if (!message) {
            return <p>{t('image-table-error')}</p>;
        }

        if (message.indexOf('Quota exceeded') > -1) {
            return <DocLink>{t('image-table-error-limit')}</DocLink>;
        }

        if (error.payload && error.payload.type === 'MoreThenMaximum') {
            return <DocLink>{t('image-table-error-big-size')}</DocLink>;
        }

        if (error.payload && error.payload.type === 'LessThenMinimum') {
            return <DocLink>{t('image-table-error-low-size')}</DocLink>;
        }

        switch (message) {
            case 'Invalid image size':
                return <DocLink>{t('image-table-error-wrong-size')}</DocLink>;
            case 'Invalid image':
                return t('image-table-error-incorrect-image');
            default:
                return t('image-table-error');
        }
    };

    return (
        <table {...b('items')} cellSpacing="0">
            <tbody>
                {items.map(({ item, error }, i) =>
                    item ? (
                        <tr key={i} {...b('item')}>
                            <td {...b('item-image')}>
                                <div
                                    {...b('image')}
                                    style={{
                                        backgroundImage: `url(${avatarsUrl}/get-dialogs-skill-card/${item.id}/orig)`,
                                    }}
                                />
                            </td>
                            <td {...b('item-createdAt')}>
                                <p>{moment(item.createdAt).format('DD.MM.YYYY')}</p>
                                <p>{formatBytes(item.size)}</p>
                            </td>
                            <td {...b('item-code')}>
                                <CopyField text={item.id} buttonLabel={t('image-table-copy')} label="" border={false} />
                            </td>
                            <td {...b('item-actions')}>
                                <Button theme="normal" size="s" onClick={() => remove(item.id)}>
                                    {t('image-table-delete')}
                                </Button>
                            </td>
                        </tr>
                    ) : (
                        <tr key={i} {...b('item', { uploading: true })}>
                            <td {...b('item-image')}>
                                {error ? <div {...b('error')}>!</div> : <Spin size="m" progress />}
                            </td>
                            <td {...b('item-createdAt')} />
                            <td {...b('item-code')}>{error ? renderError(error) : null}</td>
                            <td {...b('item-actions')} />
                        </tr>
                    ),
                )}
            </tbody>
        </table>
    );
};

export default ImageTable;
